import { motion, useReducedMotion } from "motion/react";
import type { ComponentProps, ReactNode } from "react";
import { FEDER } from "./federn";

/** Chip einer Beispielfrage (W22d-w6, wie PGChipStil der iOS-App): Flaeche --pg-chip mit Rand
 *  --pg-chip-border (mindestens 3:1), Text 15/500 in --pg-accent-text, mittig und umbrechend, nur so
 *  breit wie der Text, mindestens 44 hoch; Rundung halbe Zielhoehe (22, einzeilig eine Kapsel).
 *  Ueberfahren und gedrueckt --pg-chip-pressed, gedrueckt kurz 0,97 (bei reduzierter Bewegung nur die
 *  Flaeche); deaktiviert 40 %; Fokus 2 px Akzent (global :focus-visible). Nicht der Status-Chip aus
 *  Badge.tsx, der nie interaktiv ist. */
export function BeispielChip({ children, className = "", ...rest }: Omit<ComponentProps<typeof motion.button>, "children"> & { children: ReactNode }) {
  const ruhig = useReducedMotion();
  return (
    <motion.button
      type="button"
      whileTap={ruhig || rest.disabled ? undefined : { scale: 0.97, transition: FEDER.druecken }}
      transition={FEDER.loslassen}
      className={`beispiel-chip t-chip inline-flex min-h-(--touch) max-w-full items-center justify-center rounded-chip-zeile border border-pg-chip-border bg-pg-chip px-4 py-2 text-center text-pg-accent-text transition-colors duration-(--pg-dur-fast) hover:bg-pg-chip-pressed active:bg-pg-chip-pressed disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

/** Die Beispielfragen als Chips, zentriert und umbrechend, eingerueckt wie der Kopf (Gaist und leerer Chat, wie BeispielChips
 *  der iOS-App). `kennung`: Praefix, je Chip mit der Nummer dahinter (`assistent.beispiel.0`). */
export function BeispielChips(props: { fragen: readonly string[]; kennung: string; waehlen: (frage: string) => void }) {
  return (
    <div className="beispiel-chips flex flex-wrap justify-center gap-2 px-4">
      {props.fragen.map((f, i) => (
        <BeispielChip key={f} data-testid={`${props.kennung}.${i}`} onClick={() => props.waehlen(f)}>
          {f}
        </BeispielChip>
      ))}
    </div>
  );
}

/** Abschnitt „Zum Beispiel“ wie in der iOS-App: Kopf 13/600 in Text 2, die Chips, darunter der Hinweis
 *  zur Beratungsgrenze (13 in Text 2). `kiSatz`: erste Zeile des Fusstexts vor der Grenze (W69, D-230). W73c (D-286 H2):
 *  ohne die ungenutzten Optionen `gesperrt` und `ohneHinweis` (seit W73 nur noch in der Uebersicht). */
export function Beispiele(props: { fragen: readonly string[]; kennung: string; kiSatz?: string; waehlen: (frage: string) => void; className?: string }) {
  return (
    <section className={`flex flex-col gap-2 ${props.className ?? ""}`} aria-labelledby={`${props.kennung}-kopf`}>
      <h2 id={`${props.kennung}-kopf`} className="t-footnote t-footnote-stark px-4 text-pg-text-2">
        Zum Beispiel
      </h2>
      <BeispielChips fragen={props.fragen} kennung={props.kennung} waehlen={props.waehlen} />
      {props.kiSatz && (
        <p className="t-footnote px-4 text-pg-text-2" data-testid={`${props.kennung}.ki`}>
          {props.kiSatz}
        </p>
      )}
      <p className="t-footnote px-4 text-pg-text-2" data-testid={`${props.kennung}.hinweis`}>
        {BERATUNGSGRENZE}
      </p>
    </section>
  );
}

/** Wortlaut wie in der iOS-App (AssistentAnsicht, Fusstext unter den Beispielen). */
const BERATUNGSGRENZE = "Dein Gaist antwortet mit Quellen aus deinen Briefen. Er gibt einen Überblick, keine Rechts-, Steuer- oder Finanzberatung.";
